import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Image, ScrollView, StyleSheet, Text, View } from 'react-native'
import {
  Button,
  COLORS,
  COMMON_STYLES,
  DefaultProductImage,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { AxiosError } from 'axios'
import { DemoReviewRequestScreenProps, ROUTES } from 'app/routes/types'
import createDemoMaterialRequest from 'app/apis/createDemoMaterialRequest'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { navigationRef } from 'app/utils/RootNavigation'
import { CommonActions } from '@react-navigation/native'
import {
  ReviewIssueCropIcon,
  ReviewIssueLocationIcon,
  ReviewIssuePhoneIcon,
  ReviewIssueUserIcon,
} from 'app/icons/ReviewIssueIcons'

const DemoReviewRequest = ({ route, navigation }: DemoReviewRequestScreenProps) => {
  const { t } = useTranslation()
  const { draft } = route.params
  const [submitting, setSubmitting] = useState(false)
  const issueAddress = [
    draft.address,
    draft.block_name,
    draft.district_name,
    draft.state_name,
    draft.pin_code,
  ]
    .filter(Boolean)
    .join(', ')

  useEffect(() => {
    if (!draft.product_variant_id) {
      ToastAndroid.show(t('demo_create_select_variant_first'), ToastAndroid.SHORT)
      navigation.replace(ROUTES.DEMO_PRODUCT_SELECTION)
      return
    }
    if (!draft.vendor_id) {
      ToastAndroid.show(t('demo_select_dc_first'), ToastAndroid.SHORT)
      navigation.replace(ROUTES.DEMO_CREATE_REQUEST, { draft })
    }
  }, [draft, navigation, t])

  const submit = async () => {
    try {
      setSubmitting(true)
      await createDemoMaterialRequest({
        vendor_id: Number(draft.vendor_id),
        product_variant_id: draft.product_variant_id,
        farmer_name: draft.farmer_name,
        farmer_mobile: draft.farmer_mobile,
        units: draft.units,
        address: draft.address,
        pin_code: draft.pin_code,
        state_id: draft.state_id,
        district_id: draft.district_id,
        block_id: draft.block_id,
        village_id: draft.village_id,
        crop_id: draft.crop_id,
        inventory_node_id: draft.inventory_node_id,
        remarks: draft.remarks,
      })
      navigationRef.dispatch(
        CommonActions.reset({
          index: 1,
          routes: [
            {
              name: ROUTES.DASHBOARD,
            },
            {
              name: ROUTES.REQUEST_SUCCESS,
              params: { flow: 'DEMO_MATERIAL' },
            },
          ],
        }),
      )
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>
      ToastAndroid.show(
        axiosError.response?.data?.detail || 'Unable to create request',
        ToastAndroid.SHORT,
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <View style={[COMMON_STYLES.flex1, styles.screen]}>
      <ScrollView contentContainerStyle={styles.container}>
        <Section title="Partner Details">
          <View style={styles.partnerTop}>
            <View style={styles.partnerLogo}>
              <Text style={styles.partnerLogoText}>D</Text>
            </View>
            <View style={COMMON_STYLES.flex1}>
              <Text style={styles.partnerName}>{draft.vendor_name || '-'}</Text>
              <Text style={styles.partnerSub}>
                {draft.partner_location || draft.partner_address || '-'}
              </Text>
            </View>
          </View>
          <View style={styles.divider} />
          <Row label="Partner Id:" value={draft.partner_id || '-'} compact />
          <Row label="Node:" value={draft.node_name || '-'} compact />
          <Row label="Address:" value={draft.partner_address || '-'} compact />
        </Section>

        <Section title="Demo Product">
          <View style={styles.productRow}>
            <Image
              source={
                draft.product_image
                  ? { uri: draft.product_image }
                  : (DefaultProductImage as any)
              }
              style={styles.productImage}
            />
            <View style={COMMON_STYLES.flex1}>
              <Text style={styles.productBrand}>{draft.product_brand || '-'}</Text>
              <Text style={styles.productName}>{draft.product_name || '-'}</Text>
              <Text style={styles.productVariant}>
                {draft.product_variant_label || '-'} X {draft.units}
              </Text>
            </View>
          </View>
        </Section>

        <Section title="Issue To">
          <View style={styles.issueTop}>
            <View style={styles.issueCol}>
              <View style={styles.issueValueRow}>
                <View style={styles.issueIconSlot}>
                  <ReviewIssueUserIcon />
                </View>
                <Text style={styles.issueValue}>{draft.farmer_name || '-'}</Text>
              </View>
            </View>
            <View style={styles.issueVerticalDivider} />
            <View style={styles.issueCol}>
              <View style={styles.issueValueRow}>
                <View style={styles.issueIconSlot}>
                  <ReviewIssuePhoneIcon />
                </View>
                <Text style={styles.issueValue}>{draft.farmer_mobile || '-'}</Text>
              </View>
            </View>
          </View>
          <View style={styles.issueLine}>
            <View style={styles.issueIconSlot}>
              <ReviewIssueLocationIcon />
            </View>
            <Text style={styles.issueDetail}>{issueAddress || '-'}</Text>
          </View>
          <View style={styles.issueLine}>
            <View style={styles.issueIconSlot}>
              <ReviewIssueCropIcon />
            </View>
            <Text style={styles.issueDetail}>{draft.crop_name || '-'}</Text>
          </View>
        </Section>
      </ScrollView>
      <View style={styles.footer}>
        <Button
          text="Submit Request"
          size={SIZE.large}
          disabled={submitting}
          onClick={submit}
        />
      </View>
      {submitting && <Spinner type="on-screen" />}
    </View>
  )
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
)

const Row = ({
  label,
  value,
  compact,
}: {
  label: string
  value: string
  compact?: boolean
}) => (
  <View style={[styles.row, compact && styles.rowCompact]}>
    <Text style={[styles.label, compact && styles.labelCompact]}>{label}</Text>
    <Text style={[styles.value, compact && styles.valueCompact]}>{value}</Text>
  </View>
)

const styles = StyleSheet.create({
  screen: {},
  container: { paddingTop: 8, paddingBottom: 88 },
  section: {
    marginBottom: 8,
    padding: 16,
    backgroundColor: COLORS.white,
  },
  sectionTitle: {
    ...TYPOGRAPHY.SUBHEADING3,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
    marginBottom: 8,
  },
  row: { marginBottom: 6, ...COMMON_STYLES.row },
  rowCompact: { marginBottom: 4 },
  label: { ...TYPOGRAPHY.CAPTION1, ...TEXT_STYLES.textNeutral70 },
  labelCompact: { ...TYPOGRAPHY.CAPTION2, width: 80 },
  value: { ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textNeutral90 },
  valueCompact: { ...TYPOGRAPHY.CAPTION1 },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: COLORS.white,
    borderTopWidth: 1,
    borderTopColor: COLORS.neutral20,
  },
  partnerTop: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    columnGap: 8,
  },
  partnerLogo: {
    width: 26,
    height: 26,
    borderRadius: 4,
    backgroundColor: COLORS.primary10,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyCenter,
  },
  partnerLogoText: { ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textPrimary100 },
  partnerName: {
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
  },
  partnerSub: { ...TYPOGRAPHY.CAPTION1, ...TEXT_STYLES.textNeutral70 },
  divider: { height: 1, backgroundColor: COLORS.neutral20, marginVertical: 8 },
  productRow: { ...COMMON_STYLES.row, columnGap: 10 },
  productImage: {
    width: 85,
    height: 85,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.neutral20,
  },
  productBrand: { ...TYPOGRAPHY.CAPTION1, ...TEXT_STYLES.textNeutral70 },
  productName: {
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
    marginTop: 2,
  },
  productVariant: {
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
  },
  issueTop: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: COLORS.neutral20,
    marginBottom: 8,
  },
  issueCol: { ...COMMON_STYLES.flex1 },
  issueVerticalDivider: {
    width: 1,
    height: 24,
    backgroundColor: COLORS.neutral20,
    marginHorizontal: 12,
  },
  issueValue: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
  },
  issueValueRow: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    columnGap: 6,
  },
  issueIconSlot: {
    width: 24,
    height: 24,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyCenter,
  },
  issueLine: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    columnGap: 8,
    marginBottom: 4,
  },
  issueDetail: { ...TYPOGRAPHY.CAPTION1, ...TEXT_STYLES.textNeutral90 },
})

export default DemoReviewRequest
